/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable prefer-rest-params */
/* eslint-disable prefer-spread */
/* eslint-disable @typescript-eslint/no-unused-expressions */
import { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import type { ChangeEvent } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { Gift } from 'lucide-react';
import Header from './components/Header';
import StepOne from './components/steps/StepOne';
import StepNavigation from './components/StepNavigation';
import StepTwo, { type Child } from './components/steps/StepTwo';
import StepThree from './components/steps/StepThree';
import PaymentsCards from './components/PaymentsCard';
import ChangePlanModal from './components/ChangePlanModal';
import { CouponField } from './components/CouponField';

import TurnstileWidget from './components/TurnstileWidget';
import { ReferralModal } from './components/modals/modal-code';
import { ClientService } from './services/client.service';
import { ValidadeService } from './services/validade.service';
import { ChildService } from './services/child.service';
import { CouponService } from './services/coupon.service';
import {
  avisoCupomForaDaForma,
  cupomDaForma,
  cupomSemFormaDisponivel,
  mensagemErroCupom,
} from './utils/coupon-restrictions';
import { PlansService, type PlansPayload } from './services/plans.service';

import { UiSettingsService, type UiSettings } from './services/ui-settings.service';
import { openReplayService } from './services/openreplay.service';
import { WebhookService } from './services/webhook.service';
import PaymentMethodSwitch, { type PaymentMethod } from './components/PaymentMethodSwitch';
import PaymentsPix from './components/PaymentsPix';
import { setCheckoutStateGetter } from './services/error-tracking.service';
import { ErrorBoundary } from './components/ErrorBoundary';
import { useSessionTracking } from './hooks/useSessionTracking';
import { isInApp } from './utils/inApp';
import { getMetaClickIds, leadEventId } from './utils/metaClickIds';
import { parsePrice, planCommitment } from './utils/renewal-plans';
import {
  formasNoCheckout,
  pixNoCheckout,
  planoVendavelNoCheckout,
  precoNoPix,
  rotuloDoPlano,
} from './utils/checkout-plans';
import { diferencaDeUpgrade, formatarReais, termosDaAssinatura } from './utils/termos-assinatura';
import { DEFAULT_PLAN_SLUG, REFERRAL_ENABLED, SKIP_TURNSTILE } from './config/features';

declare global {
  interface Window {
    fbq?: (...args: any[]) => void;
    fbPixelInitialized?: boolean;
    dataLayer: Record<string, unknown>[];
    ttq?: { track: (event: string, data?: Record<string, unknown>) => void; page: () => void; load: (id: string) => void };
  }
}

function App() {
  const navigate = useNavigate();
  const location = useLocation();
  const { plano: planoSlug } = useParams<{ plano: string }>();
  const inApp = isInApp();
  const [plans, setPlans] = useState<PlansPayload[]>([]);
  const [planoSelecionado, setPlanoSelecionado] = useState<(PlansPayload & { discountedPrice?: string }) | null>(null);
  const [isAnual, setIsAnual] = useState(false);
  const [uiSettings, setUiSettings] = useState<UiSettings | null>(null);
  const [loadError, setLoadError] = useState(false);
  // Slug de plano com venda bloqueada (plans.sale_blocked): tela de indisponível.
  const [planoIndisponivel, setPlanoIndisponivel] = useState(false);
  const [timeLeft, setTimeLeft] = useState<number | null>(null);
  const [timerEnded, setTimerEnded] = useState(false);

  // Estado não persistente - sempre reinicia
  const [formData, setFormData] = useState<{
    name: string;
    whatsapp: string;
    referralCode: string | null;
  }>({
    name: '',
    whatsapp: '',
    referralCode: null,
  });

  const [deliveryData, setDeliveryData] = useState({
    cep: '',
    rua: '',
    bairro: '',
    cidade: '',
    estado: '',
    numero: '',
    complemento: '',
    recebedor: '',
  });

  const [modalOpen, setModalOpen] = useState(false);
  const [clientId, setClientId] = useState<string | null>(null);
  const [step, setStep] = useState(1);
  const [acceptTerms, setAcceptTerms] = useState(true);
  const [whatsappOptIn, setWhatsappOptIn] = useState(false);
  const [childrenData, setChildrenData] = useState<Child[]>([]);
  const [couponInput, setCouponInput] = useState('');
  const [couponApplied, setCouponApplied] = useState<any>(null);
  const [couponError, setCouponError] = useState(false);
  const [couponErrorMessage, setCouponErrorMessage] = useState<string | null>(null);
  const [mobileCouponOpen, setMobileCouponOpen] = useState(false);
  const [referralCode, setReferralCode] = useState<string | null>(null);
  const [referralModalOpen, setReferralModalOpen] = useState(false);
  const [addressSaved, setAddressSaved] = useState(false);
  const [paymentMethod, setPaymentMethod] = useState<PaymentMethod>('credit-card');
  const [apiError, setApiError] = useState<string | null>(null);
  const [, setIsLoading] = useState(false);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [email, setEmail] = useState('');
  const lastSavedEmailRef = useRef('');
  const [turnstileToken, setTurnstileToken] = useState<string>('');
  // #128: exigência de Turnstile vem do plano (plans.requires_turnstile), não do slug.
  const requireTurnstile = planoSelecionado?.requiresTurnstile === true && !SKIP_TURNSTILE;
  // Track session and steps
  useSessionTracking(step, formData);

  const clearCheckoutData = () => {
    // Função mantida para compatibilidade mas não faz nada
    // pois não usamos mais localStorage
  };

  const clearAllCheckoutData = () => {
    clearCheckoutData();
  };

  const validateEmail = (value: string): boolean => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(value) && value.length > 0;
  };

  // Salva o e-mail no placeholder da sessão e devolve o clientId pra cobrança. Precisa
  // rodar antes do pagamento: o backend lê o e-mail pelo clientId na hora de cobrar.
  // Re-dispara webhook/OpenReplay só quando o e-mail muda.
  // #362: e-mail já cadastrado NÃO troca o clientId. O backend guarda o e-mail no
  // placeholder e resolve o cadastro existente sozinho na criação do pagamento — o
  // navegador nunca conhece o id de outro cadastro.
  const ensureEmailSaved = async (emailValue: string): Promise<string> => {
    const trimmed = emailValue.trim();
    if (!clientId) throw new Error('clientId ausente');
    const effectiveId = clientId;
    if (trimmed && trimmed !== lastSavedEmailRef.current) {
      if (!validateEmail(trimmed)) throw new Error('e-mail inválido');
      await ClientService.update(clientId, { email: trimmed });

      lastSavedEmailRef.current = trimmed;
      openReplayService.setUserID(trimmed);
      openReplayService.setUserMetadata({ email: trimmed, step: 'email_confirmed' });
      WebhookService.dispatchCheckoutEmailConfirmed(
        { id: effectiveId, name: formData.name, whatsapp: formData.whatsapp },
        { id: planoSelecionado!.id, name: planoSelecionado!.name, price: planoSelecionado!.price, billingCycle: isAnual ? 'annual' : 'monthly' },
      );
    }
    return effectiveId;
  };

  const precoOriginal = parsePreco(planoSelecionado?.discountedPrice);
  // Formas do plano no checkout público (utils/checkout-plans): a aba do PIX e
  // o #512 (`cupomSemFormaDisponivel`) leem a MESMA régua.
  const allowsPix = formasNoCheckout(planoSelecionado).includes('pix');
  // Contrato anual PAGO POR MÊS (monthly_annual/annual_installment — planos
  // ocultos do disparo de reativação): cobra todo mês, então isAnual segue
  // false, mas não é "Mensal" — é fidelidade de 12 meses. Muda só rótulo e o
  // modal de troca (renovação nunca desce); preço e cobrança ficam iguais.
  const isAnualPagoPorMes =
    !!planoSelecionado &&
    planCommitment({ billingCycle: planoSelecionado.billingCycle, planType: planoSelecionado.planType ?? null }) === 1;

  // #512: o cupom que vale NESTA forma de pagamento. Cupom só-PIX na aba do
  // cartão (ou o inverso) não entra no preço nem no POST: a aba mostra o preço
  // cheio com o aviso. Quem recusa de verdade é o servidor.
  const cupomEfetivo = cupomDaForma(couponApplied, paymentMethod);
  const avisoCupom = avisoCupomForaDaForma(couponApplied, paymentMethod);

  let precoFinalNumber = precoOriginal;

  if (cupomEfetivo) {
    precoFinalNumber =
      cupomEfetivo.discount_type === 'PERCENT'
        ? precoOriginal * (1 - cupomEfetivo.value / 100)
        : Math.max(0, precoOriginal - cupomEfetivo.value);
  }

  const tipoPlano = isAnual ? 'Anual' : 'Mensal';

  const precoFinal = tipoPlano == 'Anual' ? formatPreco(precoFinalNumber / 12) : formatPreco(precoFinalNumber);

  // PIX à vista do ano (plano com `pixAVista`): no PIX o cliente paga o ano
  // INTEIRO de uma vez, então o valor do PIX e o resumo mostram o total com
  // cupom; no cartão segue a parcela (12x). Mensal puro: o mês, como sempre.
  const pixAVista = paymentMethod === 'pix' && pixNoCheckout(planoSelecionado) === 'anual_a_vista';
  const pixDoPlano = precoNoPix(planoSelecionado, precoFinalNumber);
  const precoDoPix = pixDoPlano ? formatPreco(pixDoPlano.valor) : precoFinal;

  // Termos da assinatura (recusa da Google Play, 28/10/2026): a barra fixa do
  // celular, o resumo e o quadro acima do botão de pagar leem a MESMA régua.
  // SÓ DENTRO DO APP (WebView), que é o que a Play revisa. Na web voltou o de
  // antes (anual pelo valor do mês): com o ano em destaque, quem abria o
  // checkout e preenchia o passo 1 caiu de ~55% pra ~30% (28–29/10/2026).
  const termos =
    planoSelecionado && inApp
      ? termosDaAssinatura({
          plano: planoSelecionado,
          forma: paymentMethod,
          precoTabela: precoOriginal,
          precoComCupom: precoFinalNumber,
        })
      : null;
  // Cupom no plano cobrado por mês: no CARTÃO desconta só a 1ª cobrança (o cron
  // cobra o cheio); no PIX vale todo mês (o PIX do mês sai do paidAmount).
  const cupomSoNoPrimeiroMes =
    inApp && tipoPlano === 'Mensal' && paymentMethod === 'credit-card' && precoOriginal - precoFinalNumber > 0.005;
  // PIX mensal: o resumo fala do PIX deste mês, não de um "/ mês" fixo. O PIX de
  // ex-assinante pode virar "retomar" (vínculo implícito) e os PIX seguintes
  // saem do valor da assinatura anterior — a página não sabe (termos-assinatura).
  const pixMensal = inApp && tipoPlano === 'Mensal' && paymentMethod === 'pix';

  // Modal "Mude para o Explorador" (StepTwo): a diferença REAL de preço, na
  // unidade de cobrança. Antes era "R$ R$10,00"/"R$ R$20,00" fixo, sem dizer
  // se era por mês ou por ano. Mesmo slug que o StepTwo navega.
  const slugDoUpgrade = `explorador-${(planoSlug ?? '').includes('anual') ? 'anual' : 'mensal'}`;
  const planoDoUpgrade = plans.find((p) => p.slug === slugDoUpgrade);
  const diferencaUpgrade = planoSelecionado
    ? diferencaDeUpgrade(
        { billingCycle: planoSelecionado.billingCycle, planType: planoSelecionado.planType, precoTabela: precoOriginal },
        // Sem discountedPrice no alvo, nenhum valor: o originalPrice (preço "De")
        // superestimaria a diferença.
        planoDoUpgrade?.discountedPrice != null
          ? {
              billingCycle: planoDoUpgrade.billingCycle,
              planType: planoDoUpgrade.planType,
              precoTabela: parsePrice(planoDoUpgrade.discountedPrice),
            }
          : null,
        // Na web o anual fala pelo mês, como o resto da página.
        { anualPorMes: !inApp },
      )
    : null;

  const Checkbox = ({
    checked,
    onChange,
    id,
  }: {
    checked: boolean;
    onChange: (e: ChangeEvent<HTMLInputElement>) => void;
    id: string;
  }) => (
    <div className="relative">
      <input type="checkbox" id={id} checked={checked} onChange={onChange} className="peer hidden" />
      <div
        className={`
        w-6 h-6 min-w-6 rounded-md border-2 flex items-center justify-center
        transition-all duration-200
        peer-checked:bg-blue-500 peer-checked:border-blue-500
        peer-unchecked:bg-white peer-unchecked:border-gray-300
        border-gray-300
      `}
      >
        {checked && (
          <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" strokeWidth="3" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
          </svg>
        )}
      </div>
    </div>
  );

  function parsePreco(precoStr?: string): number {
    if (!precoStr || typeof precoStr !== 'string') return 0;
    return parseFloat(precoStr.replace('R$', '').replace(',', '.'));
  }

  function formatPreco(valor: number): string {
    return `R$ ${valor.toFixed(2).replace('.', ',')}`;
  }

  async function applyCoupon() {
    const code = couponInput.trim().toUpperCase();
    console.log('🎫 Aplicando cupom:', {
      originalInput: couponInput,
      trimmedCode: code,
      codeLength: code.length,
      timestamp: new Date().toISOString(),
    });

    if (!code) return setCouponError(true);
    try {
      const coupon = await CouponService.validate(code, planoSelecionado?.id);
      const inutilizavel = cupomSemFormaDisponivel(coupon, formasNoCheckout(planoSelecionado));
      if (inutilizavel) {
        setCouponApplied(null);
        setCouponErrorMessage(inutilizavel);
        setCouponError(true);
        return;
      }

      setCouponApplied(coupon);
      setCouponError(false);
      setCouponErrorMessage(null);

      // OpenReplay tracking
      openReplayService.setUserMetadata({
        coupon: coupon.code,
        couponDiscount: coupon.discount_type === 'PERCENT' ? `${coupon.value}%` : `R$${coupon.value}`,
      });
      openReplayService.trackEvent('coupon_applied', {
        couponCode: coupon.code,
        discountType: coupon.discount_type,
        discountValue: coupon.value,
      });
    } catch (error) {
      console.error('Erro ao aplicar cupom:', error); // Debug
      setCouponApplied(null);
      setCouponErrorMessage(mensagemErroCupom(error));
      setCouponError(true);

      // OpenReplay tracking de erro
      openReplayService.trackEvent('coupon_error', {
        couponCode: code,
        error: error instanceof Error ? error.message : 'Unknown error',
      });
    }
  }

  function removeCoupon() {
    setCouponApplied(null);
    setCouponInput('');
    setCouponError(false);
    setCouponErrorMessage(null);

    // Não precisa mais limpar localStorage
  }

  function handleCouponInputChange(value: string) {
    setCouponInput(value.toUpperCase());
    if (couponError) setCouponError(false);
  }

  // Código de indicação adicionado no step de pagamento: o cliente já foi criado
  // sem referredBy no step 1, então persiste via PATCH antes do Finalizar.
  async function handlePaymentReferralConfirm(code: string) {
    setReferralCode(code);
    setReferralModalOpen(false);
    if (clientId) {
      try {
        await ClientService.update(clientId, { referredBy: code });
        openReplayService.trackEvent('referral_added_at_payment', { code });
      } catch (error) {
        console.error('Erro ao salvar código de indicação no cliente:', error);
      }
    }
  }

  const handleChildrenSubmit = async (children: Child[]) => {
    console.log('🔄 handleChildrenSubmit - Iniciando transição Step 2 → 3', {
      clientId,
      childrenCount: children.length,
      hasPlano: !!planoSelecionado,
      formData,
    });

    if (!clientId) {
      console.error('❌ clientId não encontrado');
      return alert('ID do cliente não encontrado.');
    }
    if (children.length < 1 || children.length > 2) {
      console.error('❌ Número inválido de filhos:', children.length);
      return alert('Cadastre de 1 a 2 filhos.');
    }

    setIsTransitioning(true);
    try {
      // Sempre POST no placeholder da sessão, nunca PATCH por id: o backend devolve a
      // mesma criança quando nome+nascimento já existem no client (Voltar + Continuar
      // não duplica). #362: depois de uma tentativa de pagamento as crianças da sessão
      // já foram levadas pro cadastro existente — editar pelo id antigo mexeria nesse
      // cadastro por uma rota pública (o gate recusa em `enforce`).
      const promises = children.map((child) => {
        const data = {
          name: child.name,
          gender: child.gender || undefined,
          birthdate: child.birthDate,
          clientId: clientId!,
        };
        console.log('📤 Salvando criança:', data);
        return ChildService.create(data);
      });

      const saved = await Promise.all(promises);

      // Refactor client→sub: preserva id retornado do POST/PATCH pra mandar como
      // childIds no checkout (subscriber vincula child à sub criada via webhook).
      const childrenWithIds = children.map((child, i) => ({
        ...child,
        id:
          (saved[i] as any)?.id ||
          (saved[i] as any)?.data?.id ||
          (child as any).id,
      }));

      console.log('✅ Filhos salvos com sucesso', childrenWithIds.map(c => c.id));
      setChildrenData(childrenWithIds);

      console.log('✅ Avançando para Step 3');
      setStep(3);

      // Tracking separado — erros aqui não devem bloquear o checkout
      try {
        openReplayService.setUserMetadata({
          step: 'step_2_completed',
          childrenCount: children.length.toString(),
          childrenNames: children.map((c) => c.name).join(', '),
        });
        openReplayService.trackEvent('step_2_completed', {
          childrenCount: children.length,
          childrenNames: children.map((c) => c.name),
        });

        WebhookService.dispatchCheckoutChildrenDataCompleted(
          { id: clientId!, name: formData.name, whatsapp: formData.whatsapp },
          { id: planoSelecionado!.id, name: planoSelecionado!.name, price: planoSelecionado!.price, billingCycle: isAnual ? 'annual' : 'monthly' },
          children,
        );
      } catch (trackingError) {
        console.warn('Erro no tracking (não bloqueia checkout):', trackingError);
      }
    } catch (error: any) {
      console.error('❌ Erro ao salvar dados dos filhos:', error);
      const errorMessage = error?.response?.data?.message || 'Erro ao salvar os dados dos filhos.';
      alert(errorMessage);

      openReplayService.trackEvent('step_2_error', {
        error: error instanceof Error ? error.message : 'Unknown error',
      });
    } finally {
      setIsTransitioning(false);
    }
  };

  useEffect(() => {
    async function fetchData() {
      try {
        setLoadError(false);
        setPlanoIndisponivel(false);

        if (!planoSlug) {
          // Sem slug (ex: link de indicação /?ref=X): cai no plano default mas abre a
          // seleção de planos por cima — a pessoa escolhe, não herda plano imposto.
          navigate(
            { pathname: `/${DEFAULT_PLAN_SLUG}`, search: window.location.search },
            { replace: true, state: { choosePlan: true } },
          );
          return;
        }

        // Se há slug, buscar o plano específico (timeout de 15s)
        const plan = await Promise.race([
          PlansService.getPlanBySlug(planoSlug),
          new Promise<never>((_, reject) =>
            setTimeout(() => reject(new Error('Timeout ao carregar plano')), 15000),
          ),
        ]);
        if (!plan) {
          console.error('Plano não encontrado:', planoSlug);
          navigate(
            { pathname: `/${DEFAULT_PLAN_SLUG}`, search: window.location.search },
            { replace: true, state: { choosePlan: true } },
          );
          return;
        }
        // Venda bloqueada: em vez do formulário (que o POST /payments recusaria
        // no fim), a tela de indisponível com o caminho pros planos à venda. Sem
        // AddToCart nem cupom/indicação da URL.
        if (!planoVendavelNoCheckout(plan)) {
          setPlanoSelecionado(null);
          setPlanoIndisponivel(true);
          return;
        }
        setPlanoSelecionado(plan);
        setIsAnual(plan.billingCycle === 'annual');

        const allPlans = await PlansService.getAll();
        setPlans(allPlans);

        // Disparar evento AddToCart quando o checkout abrir
        if (plan) {
          const price = plan.billingCycle === 'annual' ? plan.price / 12 : plan.price;
          if (window.fbq) {
            window.fbq('track', 'AddToCart', {
              content_name: plan.name,
              value: price.toFixed(2),
              currency: 'BRL',
            });
          }
          if (window.ttq) {
            window.ttq.track('AddToCart', {
              content_id: plan.id,
              content_name: plan.name,
              value: price,
              currency: 'BRL',
            });
          }

          // dataLayer - add_to_cart (GTM)
          window.dataLayer = window.dataLayer || [];
          window.dataLayer.push({ ecommerce: null });
          window.dataLayer.push({
            event: 'add_to_cart',
            ecommerce: {
              currency: 'BRL',
              value: price,
              items: [
                {
                  item_id: plan.id,
                  item_name: plan.name,
                  price: price,
                  quantity: 1,
                },
              ],
            },
          });
        }

        // Verificar se há cupom na URL e aplicá-lo automaticamente
        const urlParams = new URLSearchParams(window.location.search);
        const couponFromUrl = urlParams.get('coupon') || urlParams.get('cupom');

        if (couponFromUrl) {
          console.log('🎟️ Cupom detectado na URL:', couponFromUrl);
          try {
            const coupon = await CouponService.validate(couponFromUrl.toUpperCase(), plan.id);
            // #512: cupom só numa forma que este plano não oferece é ignorado,
            // como o cupom inválido da URL.
            if (cupomSemFormaDisponivel(coupon, formasNoCheckout(plan))) {
              throw new Error('cupom sem forma de pagamento disponível no plano');
            }
            setCouponApplied(coupon);
            setCouponInput(couponFromUrl.toUpperCase());
            setCouponError(false);
            console.log('✅ Cupom aplicado automaticamente:', coupon.code);

            // OpenReplay tracking
            openReplayService.setUserMetadata({
              coupon: coupon.code,
              couponSource: 'url',
              couponDiscount: coupon.discount_type === 'PERCENT' ? `${coupon.value}%` : `R$${coupon.value}`,
            });
            openReplayService.trackEvent('coupon_auto_applied', {
              couponCode: coupon.code,
              discountType: coupon.discount_type,
              discountValue: coupon.value,
              source: 'url',
            });
          } catch (error) {
            console.error('❌ Erro ao aplicar cupom da URL:', error);
            // Não mostrar erro se o cupom da URL for inválido, apenas ignorar
          }
        }

        // Código de indicação na URL (?ref= do link de compartilhamento do app)
        const refFromUrl = urlParams.get('ref') || urlParams.get('indicacao');
        if (refFromUrl && REFERRAL_ENABLED) {
          const normalized = refFromUrl.trim().toUpperCase();
          if (/^[A-Z0-9]{5}-\d{4}$/.test(normalized)) {
            try {
              const valid = await ValidadeService.validade(normalized);
              if (valid) {
                setReferralCode(normalized);
                openReplayService.trackEvent('referral_auto_applied', { code: normalized, source: 'url' });
              }
            } catch (error) {
              console.error('Erro ao validar código de indicação da URL:', error);
              // Código inválido na URL é só ignorado, como o cupom
            }
          }
        }
      } catch (err) {
        console.error('Erro ao buscar plano por slug', err);
        setLoadError(true);
      }
    }

    fetchData();
  }, [planoSlug, navigate]);

  // Chegada sem slug (redirect com state.choosePlan): abre a seleção de planos.
  // Limpa o state do history pra não reabrir em refresh.
  useEffect(() => {
    if ((location.state as { choosePlan?: boolean } | null)?.choosePlan) {
      setModalOpen(true);
      window.history.replaceState({ ...window.history.state, usr: null }, '');
    }
  }, [location.state]);

  // Sempre reiniciar quando mudar de plano
  useEffect(() => {
    // Resetar todos os estados quando o plano mudar
    setStep(1);
    setFormData({
      name: '',
      whatsapp: '',
      referralCode: null,
    });
    setDeliveryData({
      cep: '',
      rua: '',
      bairro: '',
      cidade: '',
      estado: '',
      numero: '',
      complemento: '',
      recebedor: '',
    });
    setClientId(null);
    setAcceptTerms(true);
    setWhatsappOptIn(false);
    setChildrenData([]);
    setCouponInput('');
    setCouponApplied(null);
    setReferralCode(null);
    setReferralModalOpen(false);
    setAddressSaved(false);
    setPaymentMethod('credit-card');
    setEmail('');
    lastSavedEmailRef.current = '';
    setTurnstileToken('');
  }, [planoSlug]);

  // Forçar método de pagamento para cartão se o plano não permitir PIX
  useEffect(() => {
    if (!allowsPix && paymentMethod === 'pix') {
      setPaymentMethod('credit-card');
    }
  }, [allowsPix, paymentMethod]);

  // Removidos os efeitos de salvamento no localStorage

  useEffect(() => {
    // OpenReplay tracking
    openReplayService.setUserMetadata({
      paymentMethod: paymentMethod,
    });
    openReplayService.trackEvent('payment_method_changed', {
      method: paymentMethod,
    });
    // TikTok Pixel - AddPaymentInfo
    try {
      if (window.ttq && planoSelecionado) {
        window.ttq.track('AddPaymentInfo', {
          content_id: planoSelecionado.id,
          content_name: planoSelecionado.name,
          value: planoSelecionado.price,
          currency: 'BRL',
        });
      }
    } catch { /* benigno */ }
  }, [paymentMethod]);

  useEffect(() => {
    // Contagem regressiva fica fora do app: a Google Play recusou o build 40
    // (28/10/2026) por compra com urgência enganosa — o relógio volta a 15:00
    // a cada abertura. Na web segue como está.
    if (!uiSettings || isInApp()) return;

    const totalMs = uiSettings.timerDurationInMinutes * 60 * 1000;
    setTimeLeft(totalMs);

    const interval = setInterval(() => {
      setTimeLeft((prev) => {
        if (!prev || prev <= 1000) {
          clearInterval(interval);
          setTimerEnded(true);
          return 0;
        }
        return prev - 1000;
      });
    }, 1000);

    return () => clearInterval(interval);
  }, [uiSettings]);

  function initFacebookPixel(pixelId: string) {
    if (typeof window === 'undefined') return;

    if (window.fbPixelInitialized) {
      return;
    }

    (function (f: any, b: Document, e: string, n?: any, t?: HTMLScriptElement, s?: Node) {
      if (f.fbq) return;

      n = f.fbq = function () {
        n.callMethod ? n.callMethod.apply(n, arguments) : n.queue.push(arguments);
      };
      if (!f._fbq) f._fbq = n;
      n.push = n;
      n.loaded = true;
      n.version = '2.0';
      n.queue = [];

      t = b.createElement(e) as HTMLScriptElement;
      t.async = true;
      t.src = 'https://connect.facebook.net/en_US/fbevents.js';
      s = b.getElementsByTagName(e)[0];
      s!.parentNode!.insertBefore(t, s);

      f.fbq('init', pixelId);
      f.fbq('track', 'PageView');
    })(window, document, 'script');

    window.fbPixelInitialized = true;
  }

  // Registra função para capturar estado do checkout no error tracker
  useEffect(() => {
    setCheckoutStateGetter(() => ({
      currentStep: step,
      formData: {
        name: formData.name,
        whatsapp: formData.whatsapp,
        hasAcceptedTerms: acceptTerms,
        hasWhatsappOptin: whatsappOptIn,
      },
      children: childrenData.map((child) => ({
        name: child.name,
        birthDate: child.birthDate,
        gender: child.gender || '',
      })),
      address: {
        // Os dados de endereço não estão disponíveis neste componente
        // mas seriam capturados se estivessem no estado
      },
      plan: planoSelecionado
        ? {
            id: planoSelecionado.id,
            name: planoSelecionado.name,
            price: planoSelecionado.price,
            billingCycle: isAnual ? 'annual' : 'monthly',
          }
        : {},
      coupon: couponApplied
        ? {
            code: couponApplied.code,
            discount: couponApplied.value,
            type: couponApplied.discount_type,
          }
        : undefined,
      payment: {
        method: paymentMethod === 'credit-card' ? 'card' : (paymentMethod as 'pix'),
      },
    }));
  }, [
    step,
    formData,
    acceptTerms,
    whatsappOptIn,
    childrenData,
    planoSelecionado,
    isAnual,
    couponApplied,
    paymentMethod,
  ]);

  useEffect(() => {
    async function fetchUiSettings() {
      try {
        const settings = await UiSettingsService.get();
        setUiSettings(settings);

        // Inicializar OpenReplay
        openReplayService.initialize();

        initFacebookPixel(settings.facebookPixel!);

        // TikTok Pixel
        if (settings.tiktokPixel) {
          try {
            const script = document.createElement('script');
            script.innerHTML = `
            !function (w, d, t) {
              try {
                w.TiktokAnalyticsObject = t;
                var ttq = w[t] = w[t] || [];
                ttq.methods = ["page", "track", "identify", "instances", "debug", "on", "off", "once", "ready", "alias", "group", "enableCookie", "disableCookie"];
                ttq.setAndDefer = function (t, e) {
                  t[e] = function () {
                    t.push([e].concat(Array.prototype.slice.call(arguments, 0)))
                  }
                };
                if (ttq.methods && ttq.methods.length) {
                  for (var i = 0; i < ttq.methods.length; i++) {
                    ttq.setAndDefer(ttq, ttq.methods[i]);
                  }
                }
                ttq.instance = function (t) {
                  var e = ttq._i[t] || [];
                  if (ttq.methods && ttq.methods.length) {
                    for (var n = 0; n < ttq.methods.length; n++) {
                      ttq.setAndDefer(e, ttq.methods[n]);
                    }
                  }
                  return e;
                };
                ttq.load = function (e, n) {
                  try {
                    var i = "https://analytics.tiktok.com/i18n/pixel/events.js";
                    ttq._i = ttq._i || {};
                    ttq._i[e] = [];
                    ttq._i[e]._u = i;
                    ttq._t = ttq._t || {};
                    ttq._t[e] = +new Date;
                    ttq._o = ttq._o || {};
                    ttq._o[e] = n || {};
                    var o = document.createElement("script");
                    o.type = "text/javascript";
                    o.async = !0;
                    o.src = i + "?sdkid=" + e + "&lib=ttq";
                    var a = document.getElementsByTagName("script")[0];
                    if (a && a.parentNode) {
                      a.parentNode.insertBefore(o, a);
                    } else {
                      document.head.appendChild(o);
                    }
                  } catch (err) {
                    console.error("Erro ao carregar TikTok Pixel:", err);
                  }
                };
                ttq.load("${settings.tiktokPixel}");
                ttq.page();
              } catch (err) {
                console.error("Erro ao inicializar TikTok Pixel:", err);
              }
            }(window, document, "ttq");
          `;
            document.head.appendChild(script);
          } catch (err) {
            console.error('Erro ao adicionar script do TikTok Pixel:', err);
          }
        }

        // UTMFY
        if (settings.utmfyToken) {
          try {
            // Verificar se o script já foi adicionado
            const existingScript = document.querySelector('script[data-utmfy-sdk]');
            if (existingScript) {
              console.log('⚠️ UTMFY SDK já está carregado, ignorando...');
              return;
            }

            console.log('🔍 Inicializando UTMFY com token:', settings.utmfyToken);
            const script = document.createElement('script');
            script.src = `https://cdn.utmify.com.br/sdk.js?token=${settings.utmfyToken}`;
            script.async = true;
            script.setAttribute('data-utmfy-sdk', 'true');
            script.id = 'utmfy-sdk';

            // Adicionar listeners para debug
            script.onload = () => {
              console.log('✅ UTMFY SDK carregado com sucesso');
              console.log('📊 Verificando se o UTMFY está disponível:', typeof (window as any).UTMFY !== 'undefined');
            };

            script.onerror = (error) => {
              console.error('❌ Erro ao carregar UTMFY SDK:', error);
              console.error('🔗 URL tentada:', script.src);
            };

            // Adicionar ao HEAD ao invés do BODY para carregar mais cedo
            document.head.appendChild(script);
            console.log('📝 Script UTMFY adicionado ao HEAD do documento');
          } catch (err) {
            console.error('❌ Erro ao inicializar UTMFY:', err);
          }
        } else {
          console.warn('⚠️ Token UTMFY não encontrado nas configurações');
        }
      } catch (err) {
        console.error('Erro ao carregar configurações visuais:', err);
      }
    }

    fetchUiSettings();
  }, []);

  return (
    <>
      {import.meta.env.VITE_SHOW_TEST_BANNER === 'true' && (
        <div className="w-full bg-red-600 text-white text-center py-2 font-bold text-sm tracking-wide">
          AMBIENTE DE TESTES
        </div>
      )}
      <div className="flex flex-col items-center justify-start w-full min-h-screen bg-[#f6fafe] h-full font-lato">
        {!inApp && (
          <div className="lg:hidden flex">
            <img className="h-12 py-2" src="/logo_clubinho_horizontal_v2.png" />
          </div>
        )}
        {uiSettings && (
          <div className="text-center w-full py-4 text-white bg-[#005d9f] font-normal text-sm lg:text-md">
            {step === 1 && uiSettings.onboardingSteps.general}
            {step === 2 && uiSettings.onboardingSteps.childData}
            {step === 3 && uiSettings.onboardingSteps.discovery}
          </div>
        )}

        {uiSettings && !inApp && (
          <div className="text-sm px-2 lg:px-0 py-4 w-full text-red-600 text-center font-medium">
            {timerEnded ? (
              <span>{uiSettings.timerExpiredText}</span>
            ) : (
              <>
                {uiSettings.timerText}{' '}
                {timeLeft !== null && timeLeft > 0 && (
                  <span className="font-bold">{new Date(timeLeft).toISOString().substring(14, 19)}</span>
                )}
              </>
            )}
          </div>
        )}

        {!inApp && <Header />}

        {/* Notificação de erro da API */}
        {apiError && (
          <div className="fixed top-4 right-4 z-50 bg-red-500 text-white px-6 py-3 rounded-lg shadow-lg max-w-md">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                  <path
                    fillRule="evenodd"
                    d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                    clipRule="evenodd"
                  />
                </svg>
                <span className="font-medium">Erro</span>
              </div>
              <button onClick={() => setApiError(null)} className="text-white hover:text-gray-200">
                <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                  <path
                    fillRule="evenodd"
                    d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                    clipRule="evenodd"
                  />
                </svg>
              </button>
            </div>
            <p className="mt-2 text-sm">{apiError}</p>
          </div>
        )}

        {/* pb mobile: barra fixa de preço/CTA (~80–120px, com a linha de frequência
            e total) cobre o fim do conteúdo sem ele */}
        <div className="flex lg:mt-10 lg:flex-row lg:gap-4 flex-col pb-36 lg:pb-0">
          <div className="w-full max-w-[700px]">
            <div className="w-full flex items-center justify-center">
              <StepNavigation currentStep={step} />
            </div>
            <div className="bg-white mt-5 rounded-2xl w-full lg:p-10 p-4">
              {!planoSelecionado ? (
                <div className="flex items-center justify-center py-20">
                  <div className="text-center w-full max-w-2xl mx-auto">
                    {loadError ? (
                      <div className="space-y-4">
                        <svg className="w-12 h-12 text-red-400 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
                        </svg>
                        <p className="text-[#595D69] text-lg font-medium">Erro ao carregar o plano</p>
                        <p className="text-[#595D69] text-sm">Verifique sua conexão e tente novamente.</p>
                        <button
                          onClick={() => window.location.reload()}
                          className="bg-[#018ED5] text-white px-8 py-3 rounded-lg hover:bg-[#0178B5] transition-colors font-medium"
                        >
                          Tentar novamente
                        </button>
                      </div>
                    ) : planoIndisponivel ? (
                      <div className="space-y-4">
                        <svg className="w-12 h-12 text-[#018ED5] mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                        <p className="text-[#595D69] text-lg font-medium">Este plano não está mais disponível</p>
                        <p className="text-[#595D69] text-sm">Escolha um dos nossos planos para continuar a assinatura.</p>
                        <button
                          onClick={() =>
                            navigate(
                              { pathname: `/${DEFAULT_PLAN_SLUG}`, search: window.location.search },
                              { replace: true, state: { choosePlan: true } },
                            )
                          }
                          className="bg-[#018ED5] text-white px-8 py-3 rounded-lg hover:bg-[#0178B5] transition-colors font-medium"
                        >
                          Ver planos disponíveis
                        </button>
                      </div>
                    ) : (
                      <motion.div
                        initial={{ opacity: 0, scale: 0.8 }}
                        animate={{ opacity: 1, scale: 1 }}
                        transition={{ duration: 0.5 }}
                        className="space-y-4"
                      >
                        <svg
                          className="animate-spin h-8 w-8 text-[#018ED5] mx-auto mb-4"
                          xmlns="http://www.w3.org/2000/svg"
                          fill="none"
                          viewBox="0 0 24 24"
                        >
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z" />
                        </svg>
                        <p className="text-[#595D69]">Carregando plano...</p>

                        {/* Skeleton do formulário */}
                        <div className="space-y-4 mt-8">
                          <div className="h-6 bg-gray-200 rounded animate-pulse w-3/4 mx-auto"></div>
                          <div className="h-4 bg-gray-200 rounded animate-pulse w-full"></div>
                          <div className="h-4 bg-gray-200 rounded animate-pulse w-5/6 mx-auto"></div>
                          <div className="h-12 bg-gray-200 rounded animate-pulse w-full"></div>
                          <div className="h-12 bg-gray-200 rounded animate-pulse w-full"></div>
                          <div className="h-12 bg-gray-200 rounded animate-pulse w-full"></div>
                        </div>
                      </motion.div>
                    )}
                  </div>
                </div>
              ) : (
                <ErrorBoundary
                  fallback={
                    <div className="bg-red-50 border border-red-200 rounded-lg p-6 text-center">
                      <h3 className="text-lg font-semibold text-red-900 mb-2">Erro inesperado</h3>
                      <p className="text-red-700 mb-4">Ocorreu um problema. Por favor, recarregue a página.</p>
                      <button
                        onClick={() => window.location.reload()}
                        className="bg-red-600 text-white px-6 py-2 rounded-lg hover:bg-red-700 transition-colors"
                      >
                        Recarregar página
                      </button>
                    </div>
                  }
                >
                <AnimatePresence mode="wait">
                  {step === 1 && (
                    <StepOne
                      key="step-1"
                      disabled={!acceptTerms}
                      requireTurnstile={requireTurnstile}
                      turnstileToken={turnstileToken}
                      referralCode={referralCode}
                      onReferralChange={setReferralCode}
                      initialName={formData.name}
                      initialWhatsapp={formData.whatsapp}
                      price={{
                        cupom: cupomEfetivo,
                        total: precoFinal,
                        rodape: termos?.rodape,
                      }}
                      onSubmit={async (data) => {
                        setIsLoading(true);
                        setApiError(null);
                        try {
                          // Voltar + Continuar não deve criar outro cliente: se já existe, atualiza
                          // #389: só a criação dispara Lead (server-side no POST /clients + fbq abaixo)
                          const novoLead = !clientId;
                          const client = clientId
                            ? await ClientService.update(clientId, {
                                name: data.name,
                                whatsapp: data.whatsapp,
                                referredBy: data.referralCode || undefined,
                              })
                            : await ClientService.create({
                                ...data,
                                // Backend credita indicação lendo referredBy; referralCode é
                                // sobrescrito na criação pelo código próprio do cliente.
                                referredBy: data.referralCode || undefined,
                                acceptedTerms: acceptTerms,
                                whatsappOptin: whatsappOptIn,
                                planId: planoSelecionado?.id,
                                ...(requireTurnstile && { turnstileToken, planSlug: planoSlug }),
                                ...getMetaClickIds(), // #389: fbc/fbp pro Lead da CAPI
                              });
                          setClientId(client.id);
                          setFormData(data);
                          setStep(2);

                          // Tracking separado — erros aqui não devem bloquear o checkout
                          try {
                            // #389: mesmo event_id do Lead que o backend manda pela CAPI no
                            // POST /clients — a Meta conta um só quando os dois chegam.
                            if (novoLead && window.fbq) {
                              window.fbq(
                                'track',
                                'Lead',
                                { currency: 'BRL' },
                                { eventID: leadEventId(client.id) },
                              );
                            }
                            if (window.fbq && planoSelecionado) {
                              window.fbq('track', 'InitiateCheckout', {
                                content_name: planoSelecionado.name,
                                value: precoOriginal.toFixed(2),
                                currency: 'BRL',
                              });
                            }
                            if (window.ttq && planoSelecionado) {
                              window.ttq.track('InitiateCheckout', {
                                content_id: planoSelecionado.id,
                                content_name: planoSelecionado.name,
                                value: precoOriginal,
                                currency: 'BRL',
                              });

                              window.dataLayer = window.dataLayer || [];
                              window.dataLayer.push({ ecommerce: null });
                              window.dataLayer.push({
                                event: 'begin_checkout',
                                ecommerce: {
                                  currency: 'BRL',
                                  value: precoOriginal,
                                  items: [
                                    {
                                      item_id: planoSelecionado.id,
                                      item_name: planoSelecionado.name,
                                      price: precoOriginal,
                                      quantity: 1,
                                    },
                                  ],
                                },
                              });
                            }

                            openReplayService.setUserMetadata({
                              clientId: client.id,
                              name: data.name,
                              phone: data.whatsapp,
                              step: 'step_1_completed',
                              plan: planoSelecionado?.name,
                              billingCycle: isAnual ? 'annual' : 'monthly',
                            });
                            openReplayService.trackEvent('step_1_completed', {
                              clientId: client.id,
                              plan: planoSelecionado?.name,
                              billingCycle: isAnual ? 'annual' : 'monthly',
                            });

                            WebhookService.dispatchCheckoutPersonalDataCompleted(
                              { id: client.id, name: data.name, whatsapp: data.whatsapp },
                              { id: planoSelecionado!.id, name: planoSelecionado!.name, price: planoSelecionado!.price, billingCycle: isAnual ? 'annual' : 'monthly' },
                            );
                          } catch (trackingError) {
                            console.warn('Erro no tracking (não bloqueia checkout):', trackingError);
                          }
                        } catch (error) {
                          console.error('Erro ao criar cliente:', error);
                          setApiError('Erro ao salvar dados do cliente. Tente novamente.');

                          openReplayService.trackEvent('step_1_error', {
                            error: error instanceof Error ? error.message : 'Unknown error',
                          });
                        } finally {
                          setIsLoading(false);
                        }
                      }}
                    />
                  )}
                  {step === 2 &&
                    (() => {
                      if (!planoSelecionado) {
                        console.error('❌ Step 2 - planoSelecionado é null, voltando para Step 1');
                        return (
                          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6 text-center">
                            <div className="w-12 h-12 bg-yellow-100 rounded-full flex items-center justify-center mx-auto mb-4">
                              <svg
                                className="w-6 h-6 text-yellow-600"
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                              >
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  strokeWidth={2}
                                  d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z"
                                />
                              </svg>
                            </div>
                            <h3 className="text-lg font-semibold text-yellow-900 mb-2">Carregando dados do plano...</h3>
                            <p className="text-yellow-700 mb-4">
                              Houve um problema ao carregar o plano. Por favor, tente novamente.
                            </p>
                            <button
                              onClick={() => {
                                console.log('🔄 Voltando para Step 1');
                                setStep(1);
                              }}
                              className="bg-yellow-600 text-white px-6 py-2 rounded-lg hover:bg-yellow-700 transition-colors"
                            >
                              Voltar ao Início
                            </button>
                          </div>
                        );
                      }

                      return (
                        <ErrorBoundary
                          fallback={
                            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6 text-center">
                              <h3 className="text-lg font-semibold text-yellow-900 mb-2">
                                Erro ao carregar esta etapa
                              </h3>
                              <p className="text-yellow-700 mb-4">
                                Ocorreu um erro inesperado. Por favor, tente novamente.
                              </p>
                              <button
                                onClick={() => {
                                  setStep(1);
                                }}
                                className="bg-yellow-600 text-white px-6 py-2 rounded-lg hover:bg-yellow-700 transition-colors"
                              >
                                Voltar ao Início
                              </button>
                            </div>
                          }
                        >
                          <StepTwo
                            key="step-2"
                            price={{
                              cupom: cupomEfetivo,
                              total: precoFinal,
                              rodape: termos?.rodape,
                            }}
                            onBack={() => setStep(1)}
                            diferencaUpgrade={diferencaUpgrade}
                            onSubmitChildren={handleChildrenSubmit}
                            initialChildren={childrenData}
                            planoSelecionado={{
                              name: planoSelecionado.name,
                              price: precoOriginal,
                            }}
                          />
                        </ErrorBoundary>
                      );
                    })()}
                  {step === 3 &&
                    (() => {
                      // Validar dados necessários para Step 3
                      const canRenderStep3 = !!(
                        planoSelecionado &&
                        clientId &&
                        formData.name &&
                        formData.whatsapp &&
                        childrenData &&
                        Array.isArray(childrenData) &&
                        childrenData.length > 0
                      );

                      console.log('🔍 Step 3 - Validação de dados:', {
                        step,
                        hasPlano: !!planoSelecionado,
                        planoNome: planoSelecionado?.name,
                        hasClientId: !!clientId,
                        clientId,
                        hasFormData: !!(formData.name && formData.whatsapp),
                        formData,
                        hasChildren: childrenData && Array.isArray(childrenData) && childrenData.length > 0,
                        childrenCount: childrenData ? childrenData.length : 0,
                        canRender: canRenderStep3,
                        isTransitioning,
                      });

                      if (isTransitioning) {
                        return (
                          <div className="flex items-center justify-center py-12">
                            <div className="text-center">
                              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-pink-600 mx-auto mb-4"></div>
                              <p className="text-gray-600">Carregando próxima etapa...</p>
                            </div>
                          </div>
                        );
                      }

                      if (!canRenderStep3) {
                        console.error('❌ Step 3 - Dados incompletos, voltando para Step 1');
                        return (
                          <div className="bg-red-50 border border-red-200 rounded-lg p-6 text-center">
                            <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                              <svg
                                className="w-6 h-6 text-red-600"
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                              >
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  strokeWidth={2}
                                  d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z"
                                />
                              </svg>
                            </div>
                            <h3 className="text-lg font-semibold text-red-900 mb-2">Dados incompletos</h3>
                            <p className="text-red-700 mb-4">
                              Alguns dados necessários não foram encontrados. Por favor, recomece o processo.
                            </p>
                            <button
                              onClick={() => {
                                console.log('🔄 Reiniciando checkout');
                                setStep(1);
                                setClientId(null);
                                setChildrenData([]);
                                setAddressSaved(false);
                              }}
                              className="bg-red-600 text-white px-6 py-2 rounded-lg hover:bg-red-700 transition-colors"
                            >
                              Voltar ao Início
                            </button>
                          </div>
                        );
                      }

                      return (
                        <ErrorBoundary
                          fallback={
                            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6 text-center">
                              <h3 className="text-lg font-semibold text-yellow-900 mb-2">
                                Erro ao carregar esta etapa
                              </h3>
                              <p className="text-yellow-700 mb-4">
                                Ocorreu um erro inesperado. Por favor, tente novamente.
                              </p>
                              <button
                                onClick={() => {
                                  setStep(2);
                                  setAddressSaved(false);
                                }}
                                className="bg-yellow-600 text-white px-6 py-2 rounded-lg hover:bg-yellow-700 transition-colors"
                              >
                                Voltar para Etapa Anterior
                              </button>
                            </div>
                          }
                        >
                          <StepThree
                            key="step-3"
                            data={formData}
                            price={{
                              cupom: cupomEfetivo,
                              total: precoFinal,
                            }}
                            onSubmitChildren={async (data) => {
                              if (!clientId) {
                                console.error('❌ clientId não encontrado no Step 3');
                                alert('ID do cliente não encontrado.');
                                return;
                              }

                              try {
                                console.log('📤 Atualizando endereço do cliente:', data);
                                // Limpar dados vazios antes de enviar
                                const cleanedData = Object.entries(data).reduce((acc, [key, value]) => {
                                  if (value !== undefined && value !== null && value !== '') {
                                    acc[key] = value;
                                  }
                                  return acc;
                                }, {} as any);

                                await ClientService.update(clientId, cleanedData);
                                setDeliveryData(data);
                                setAddressSaved(true);

                                console.log('✅ Endereço salvo com sucesso');

                                // Webhook tracking
                                WebhookService.dispatchCheckoutAddressCompleted(
                                  { id: clientId!, name: formData.name, whatsapp: formData.whatsapp },
                                  { id: planoSelecionado!.id, name: planoSelecionado!.name, price: planoSelecionado!.price, billingCycle: isAnual ? 'annual' : 'monthly' },
                                  childrenData,
                                  data,
                                );

                                // Não salva mais no localStorage
                              } catch (err: any) {
                                console.error('❌ Erro ao atualizar cliente com endereço:', err);
                                const errorMessage =
                                  err?.response?.data?.message || 'Erro ao salvar os dados de endereço.';
                                alert(errorMessage);
                              }
                            }}
                            onBack={() => {
                              console.log('⬅️ Voltando para Step 2');
                              setStep(2);
                              setAddressSaved(false);
                            }}
                            planoSelecionado={{
                              name: planoSelecionado.name,
                              price: precoOriginal,
                            }}
                          />
                        </ErrorBoundary>
                      );
                    })()}
                </AnimatePresence>
                </ErrorBoundary>
              )}
            </div>
            {/* {addressSaved && <OfferCard />} */}

            {/* Cupom no mobile — o Resumo (que tem o campo de cupom) fica hidden lg:block
                até o pagamento, então antes disso o cupom precisa de uma entrada própria. */}
            {!(addressSaved && step === 3) && (
              <div className="lg:hidden bg-white mt-5 rounded-2xl w-full p-4">
                {couponApplied || mobileCouponOpen ? (
                  <div className="flex flex-col gap-2">
                    <h2 className="text-[#595D69]">Cupom de desconto</h2>
                    <CouponField
                      couponInput={couponInput}
                      couponApplied={couponApplied}
                      couponError={couponError}
                      couponErrorMessage={couponErrorMessage}
                      onInputChange={handleCouponInputChange}
                      onApply={applyCoupon}
                      onRemove={removeCoupon}
                    />
                  </div>
                ) : (
                  <button
                    onClick={() => setMobileCouponOpen(true)}
                    className="flex items-center gap-2 text-[#018ED5] hover:opacity-90 hover:cursor-pointer"
                  >
                    <img src="/icon-tag.svg" className="w-5 h-5" alt="" /> Tem um cupom de desconto?
                  </button>
                )}
              </div>
            )}

            {addressSaved && step === 3 ? (
              <div className="bg-white mt-5 rounded-2xl w-full p-10">
                <h1 className="font-bold text-xl mb-3">Pagamento</h1>
                <span className="text-base text-[#595D69]">
                  Digite os dados de pagamento para concluir sua assinatura e começar a receber os livros.
                </span>

                {/* Segunda chance do código de indicação — pra quem passou reto no step 1 */}
                {REFERRAL_ENABLED &&
                  (referralCode ? (
                    <p className="mt-4 text-sm text-green-600">
                      Código de indicação aplicado: <span className="font-semibold">{referralCode}</span> 🎁
                    </p>
                  ) : (
                    <button
                      onClick={() => setReferralModalOpen(true)}
                      className="mt-4 flex items-center gap-2 text-[#018ED5] hover:opacity-90 hover:cursor-pointer"
                    >
                      <Gift className="w-5 h-5 shrink-0" /> Alguém te indicou? Adicionar código de indicação
                    </button>
                  ))}

                {allowsPix && (
                  <div className="mb-4">
                    <PaymentMethodSwitch
                      value={paymentMethod}
                      onChange={setPaymentMethod}
                    />
                  </div>
                )}

                {avisoCupom && (
                  <p className="text-sm text-yellow-800 bg-yellow-50 border border-yellow-200 rounded-lg p-3 mb-4">
                    {avisoCupom}
                  </p>
                )}

                {/* Renderização condicional */}
                {paymentMethod === 'credit-card' && planoSelecionado && (
                  <PaymentsCards
                    clientId={clientId!}
                    planId={planoSelecionado.id}
                    coupon={cupomEfetivo}
                    price={{
                      cupom: cupomEfetivo,
                      total: precoFinal,
                    }}
                    termos={termos ?? undefined}
                    childIds={childrenData.map((ch: any) => ch.id).filter(Boolean)}
                    recipient={{
                      name: formData.name,
                      whatsapp: formData.whatsapp,
                    }}
                    shipping={deliveryData}
                    email={email}
                    onEmailChange={setEmail}
                    ensureEmailSaved={ensureEmailSaved}
                  />
                )}

                {paymentMethod === 'pix' && planoSelecionado && (
                  <PaymentsPix
                    price={{
                      cupom: cupomEfetivo,
                      // Valor que o PIX cobra: o mês, ou o ano inteiro no à vista.
                      // Vai pra barra do celular e pro purchase do GTM/n8n.
                      total: precoDoPix,
                      detalhe: pixDoPlano?.detalhe ?? undefined,
                    }}
                    termos={termos ?? undefined}
                    coupon={cupomEfetivo}
                    clientId={clientId!}
                    planId={planoSelecionado.id}
                    childIds={childrenData.map((ch: any) => ch.id).filter(Boolean)}
                    recipient={{
                      name: formData.name,
                      whatsapp: formData.whatsapp,
                    }}
                    shipping={deliveryData}
                    email={email}
                    onEmailChange={setEmail}
                    ensureEmailSaved={ensureEmailSaved}
                  />
                )}
              </div>
            ) : (
              <>
                {step === 1 && requireTurnstile && (
                  <div className="mx-4 mt-2 flex justify-center">
                    <TurnstileWidget
                      onVerify={(token) => setTurnstileToken(token)}
                      onExpire={() => setTurnstileToken('')}
                      onError={() => setTurnstileToken('')}
                    />
                  </div>
                )}
                {step === 1 && (
                  <div className="py-5 mx-4 flex flex-col gap-3">
                    <label htmlFor="acceptTerms" className="flex items-start gap-3 cursor-pointer">
                      <Checkbox id="acceptTerms" checked={acceptTerms} onChange={() => setAcceptTerms(!acceptTerms)} />
                      <span className="text-sm text-[#595D69]">
                        Confirmo que li e estou de acordo com os{' '}
                        <a
                          className="hover:text-blue-900 text-blue-800 cursor-pointer"
                          href="https://coracoespreciosos.com.br/contrato"
                          target="_blank"
                        >
                          Termos de Uso
                        </a>{' '}
                        e{' '}
                        <a
                          className="hover:text-blue-900 text-blue-800 cursor-pointer"
                          href="https://coracoespreciosos.com.br/politica-de-privacidade/"
                          target="_blank"
                        >
                          Políticas de Privacidade
                        </a>{' '}
                        da plataforma Clube Corações Preciosos.
                      </span>
                    </label>

                    <label htmlFor="whatsappOptIn" className="flex items-start gap-3 cursor-pointer">
                      <Checkbox
                        id="whatsappOptIn"
                        checked={whatsappOptIn}
                        onChange={() => setWhatsappOptIn(!whatsappOptIn)}
                      />
                      <span className="text-sm text-[#595D69]">
                        Quero receber descontos e novidades por WhatsApp (opcional)
                      </span>
                    </label>
                  </div>
                )}
              </>
            )}
          </div>
          <div
            className={`
        ${addressSaved && step === 3 ? 'block' : 'hidden lg:block'}
          relative mobile:w-full bg-white rounded-2xl w-full max-w-[360px] pt-0 lg:pt-5 pb-20 px-8
          `}
          >
            <div className="flex items-center justify-between w-full">
              <h2 className="font-bold text-lg">Resumo</h2>
              <button
                onClick={() => setModalOpen(true)}
                className="hover:text-[#018ED5]/80  hover:opacity-90 hover:cursor-pointer text-[#018ED5] flex gap-1"
              >
                <img src="/icon-alter-plan.svg" />
                Alterar plano
              </button>
            </div>

            <img src="/imagem-capa.webp" className="mt-10 rounded-md" />

            {planoSelecionado && (
              <div className="bg-[#DBECFF] text-bold text-[#0168AC] my-4 py-2 px-6 text-sm rounded-full w-fit">
                {/* Anual pago por mês: o nome já diz o contrato; "- Mensal" confundia */}
                {isAnualPagoPorMes ? planoSelecionado.name : rotuloDoPlano(planoSelecionado.name, tipoPlano)}
              </div>
            )}
            <h2 className="text-lg font-bold">Clubinho Corações Preciosos</h2>
            {pixAVista ? (
              <>
                <h3 className="text-[#595D69] text-xl font-medium flex items-center gap-2">
                  {formatPreco(precoOriginal)}
                  <p className="text-sm font-light">à vista no PIX</p>
                </h3>
                <p className="text-xs text-[#595D69] mt-1">Pagamento único do ano · um PIX só, válido por 12 meses</p>
              </>
            ) : tipoPlano === 'Anual' && inApp ? (
              <>
                {/* Anual no cartão, no app: o valor COBRADO (o ano) em destaque e a
                    parcela abaixo (recusa da Google Play, 28/10/2026). Na web, o
                    valor do mês (ramo de baixo). */}
                <h3 className="text-[#595D69] text-xl font-medium flex items-center gap-2">
                  {formatarReais(precoOriginal)}
                  <p className="text-sm font-light">/ ano</p>
                </h3>
                <p className="text-xs text-[#595D69] mt-1">
                  em 12x de {formatarReais(precoOriginal / 12)} no cartão · renova automaticamente
                </p>
              </>
            ) : (
              <h3 className="text-[#595D69] text-xl font-medium flex items-center gap-2">
                {tipoPlano === 'Anual' ? formatPreco(precoOriginal / 12) : formatPreco(precoOriginal)}
                <p className="text-sm font-light">{pixMensal ? '1º mês no PIX' : '/ mês'}</p>
              </h3>
            )}
            {isAnualPagoPorMes && (
              <p className="text-xs text-[#595D69] mt-1">Contrato de 12 meses · cobrado mensalmente no cartão</p>
            )}

            <div className="border-b border-black/10 w-full my-10" />

            <div className="mb-5 flex flex-col gap-2">
              <h2 className="text-[#595D69]">Cupom de desconto</h2>

              <CouponField
                couponInput={couponInput}
                couponApplied={couponApplied}
                couponError={couponError}
                couponErrorMessage={couponErrorMessage}
                onInputChange={handleCouponInputChange}
                onApply={applyCoupon}
                onRemove={removeCoupon}
              />
            </div>

            <div className="flex items-center justify-between">
              <p>Subtotal</p>
              <span>
                {/* Subtotal é o preço de tabela; o cupom aparece em Desconto e no Total. */}
                {pixAVista ? (
                  <>{formatPreco(precoOriginal)} à vista</>
                ) : tipoPlano === 'Anual' ? (
                  inApp ? (
                    <>{formatarReais(precoOriginal)} / ano</>
                  ) : (
                    <>{formatPreco(precoOriginal / 12)} / mês</>
                  )
                ) : pixMensal ? (
                  <>{formatPreco(precoOriginal)} (1º mês)</>
                ) : (
                  <>{formatPreco(precoOriginal)} / mês</>
                )}
              </span>
            </div>

            <div className="border-b flex border-black/10 w-full border-dashed my-2" />

            <div className="flex items-center justify-between">
              <p>Frete</p>
              <span className="text-[#595D69]">Grátis</span>
            </div>

            {cupomEfetivo && (
              <>
                <div className="border-b border-black/10 w-full border-dashed my-2" />
                <div className="flex items-center justify-between">
                  <p>Desconto</p>
                  <span className="text-[#595D69]">
                    {cupomEfetivo?.discount_type === 'AMOUNT' ? 'R$' : '%'} {cupomEfetivo?.value || 0}
                  </span>
                </div>
              </>
            )}

            <div className="border-b flex border-black/10 w-full border-dashed my-2" />

            <div className="font-bold flex items-center justify-between text-lg">
              <h2>Total</h2>
              <span className="text-[#595D69]">
                {pixAVista ? (
                  <>{formatPreco(precoFinalNumber)} à vista no PIX</>
                ) : tipoPlano === 'Anual' ? (
                  inApp ? (
                    <>{formatarReais(precoFinalNumber)} / ano</>
                  ) : (
                    <>
                      {precoFinal} x 12 = {formatarReais(precoFinalNumber)}
                    </>
                  )
                ) : pixMensal ? (
                  <>{precoFinal} neste PIX</>
                ) : cupomSoNoPrimeiroMes ? (
                  <>{precoFinal} no 1º mês</>
                ) : isAnualPagoPorMes ? (
                  <>{precoFinal} / mês (12 meses)</>
                ) : (
                  <>{precoFinal} / mês</>
                )}
              </span>
            </div>
            {termos && <p className="text-xs text-[#595D69] mt-2 text-right">{termos.resumo}</p>}

            <img className="absolute -ml-8 mt-6" src="/secury-database.svg" />
          </div>
        </div>

        {modalOpen && (
          <ChangePlanModal
            isAnual={isAnual}
            toggleAnual={() => setIsAnual(!isAnual)}
            // Anual pago por mês: o modal só oferece subir (nunca mensal puro)
            planoEntrada={isAnualPagoPorMes ? planoSelecionado : null}
            onClose={() => setModalOpen(false)}
            onSelect={(planName, billingCycle) => {
              const slug = `${planName.toLowerCase()}-${billingCycle}`;
              // Limpar dados do checkout ao trocar de plano
              clearAllCheckoutData();
              // Preserva ?ref=/?coupon= — o efeito de URL re-aplica no plano novo
              navigate({ pathname: `/${slug}`, search: window.location.search });
              setModalOpen(false);
            }}
            plans={plans}
          />
        )}
        <ReferralModal
          isOpen={referralModalOpen}
          onClose={() => setReferralModalOpen(false)}
          onConfirm={handlePaymentReferralConfirm}
        />
      </div>
    </>
  );
}

export default App;
